import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';

import { generateApolloClientHeaders } from '../../../modules';
import { phoenixAccountGetWalletPaymentInfo } from '../../../modules/graph';
import { SavedCard, useSavedCardsStore } from '../../../modules/store/savedCardsStore';

interface PageContextSessionData {
  sessionData?: {
    cacheData?: {
      data?: {
        consumerID?: string;
        email?: string;
      };
    };
  };
}

export interface UseWalletSavedCardsArgs {
  pageContext: PageContextSessionData & Record<string, unknown>;
  isLocal: boolean;
  skip?: boolean;
}

interface PaymentMethod {
  id: string;
  cardNumber: string;
  cardholderName: string;
  cardType: string;
  expiryDate: string;
  isPrimary: boolean;
  cardStatus?: string;
}

export function useWalletSavedCards({ pageContext, isLocal, skip = false }: UseWalletSavedCardsArgs) {
  const isDataLoaded = useRef(false);

  const sessionData = pageContext?.sessionData?.cacheData?.data;
  const customerId = sessionData?.consumerID;

  const shouldSkip = skip || !customerId;

  // Clear store before fetching fresh data (skip if in mock mode to preserve seeds)
  const shouldSkipRef = useRef(shouldSkip);
  useEffect(() => {
    if (!shouldSkipRef.current) {
      useSavedCardsStore.getState().setProfileSavedCards(undefined as unknown as SavedCard[]);
    }
  }, []);

  const variables = useMemo(
    () => ({
      customerId,
    }),
    [customerId]
  );

  useQuery(phoenixAccountGetWalletPaymentInfo, {
    variables,
    context: generateApolloClientHeaders(isLocal, pageContext),
    fetchPolicy: 'no-cache',
    skip: shouldSkip || isDataLoaded.current,
    onCompleted: (data: Record<string, unknown>) => {
      const walletData = data?.['customer'] as Record<string, unknown> | undefined;
      const paymentMethods =
        ((walletData?.['wallet'] as Record<string, unknown>)?.['paymentMethods'] as PaymentMethod[]) ?? [];

      // Transform API response to SavedCard format
      const cards: SavedCard[] = paymentMethods.map((method: PaymentMethod) => ({
        id: method.id,
        cardNumber: method.cardNumber,
        cardholderName: method.cardholderName,
        cardType: method.cardType,
        expiryDate: method.expiryDate,
        isPrimary: method.isPrimary,
        cardStatus: method.cardStatus,
      }));

      useSavedCardsStore.getState().setProfileSavedCards(cards);
      isDataLoaded.current = true;
    },
    onError: () => {
      isDataLoaded.current = false;
      useSavedCardsStore.getState().setProfileSavedCards([]);
    },
  });
}
